// A failed action can be retried by hand up to MAX_RETRY_ATTEMPTS times. The
// first failure is the original attempt, so the count of failures runs to
// MAX_RETRY_ATTEMPTS + 1 before the retry button gives way to a support hint.
export const MAX_RETRY_ATTEMPTS = 3

export type RetryState = {
  failures: number
  // The retry the button would start next, 1-based. 0 before any failure.
  nextAttempt: number
  canRetry: boolean
  exhausted: boolean
}

export function retryState(failures: number, max = MAX_RETRY_ATTEMPTS): RetryState {
  return {
    failures,
    nextAttempt: failures > 0 && failures <= max ? failures : 0,
    canRetry: failures > 0 && failures <= max,
    exhausted: failures > max,
  }
}

// The store is injectable so the composable can pass a reactive Map.
export function createRetryLimiter(
  store: Map<string, number> = new Map(),
  max = MAX_RETRY_ATTEMPTS
) {
  return {
    state: (key: string) => retryState(store.get(key) ?? 0, max),
    recordFailure(key: string) {
      const failures = (store.get(key) ?? 0) + 1
      store.set(key, failures)
      return retryState(failures, max)
    },
    // The original attempt failed outside the limiter (an automatic load). Counts
    // once; later calls during the same failure streak change nothing.
    recordOriginalFailure(key: string) {
      if (!store.get(key)) store.set(key, 1)
      return retryState(store.get(key) ?? 0, max)
    },
    reset(key: string) {
      store.delete(key)
    },
  }
}

const NON_RETRYABLE_CODES = new Set([
  'BAD_USER_INPUT',
  'FORBIDDEN',
  'UNAUTHENTICATED',
  'NOT_FOUND',
  'GRAPHQL_VALIDATION_FAILED',
])

type ApolloLikeError = {
  networkError?: unknown
  graphQLErrors?: ReadonlyArray<{ extensions?: { code?: unknown } | null } | null>
}

// Network errors, timeouts and server faults are worth another try. Validation,
// permission and not-found errors fail the same way again, so they get no retry.
export function isRetryableError(error: unknown): boolean {
  if (!error) return false
  const { networkError, graphQLErrors } = error as ApolloLikeError
  if (networkError) return true
  const codes = (graphQLErrors ?? []).map((e) => e?.extensions?.code)
  return !codes.some((code) => typeof code === 'string' && NON_RETRYABLE_CODES.has(code))
}
